import React, { useState, useLayoutEffect } from 'react';
import './inquiry-game.css';
import {
    goBack,
    goToScreen,
    goToScreenAndReset,
    setHistoryScreenState,
} from '../../actions/router';
import { connect } from 'react-redux';
import {
    INQUIRY_GAME_SCREEN,
    MAIN_GAME_SCREEN,
    SETTINGS_SCREEN,
} from '../../constants/routes';
import { IconButton } from '../../components/icon-button';
import { TutorialModalParent } from '../../components/modal-parents/tutorial-modal';
import { ProgressUpdateModalParent } from '../../components/modal-parents/progress-update-modal';
import { Panel } from '../../components/panel';
import { Heading } from '../../components/heading';
import { TopicDecision } from '../../components/topic-decision';
import { CommitteeSelection } from './committee-selection';
import { completeTutorial } from '../../actions/tutorials';
import { progressTime, updateOverallScores } from '../../actions/active-game';
import {
    getCommitteeGameScores,
    getCommitteeScores,
} from '../../helpers/inquiry';
import classNames from 'classnames';
import Copy from '../../copy/inquiry';
const steps = {
    0: 'topic-decision',
    1: 'committee-selection',
};

const InquiryGameScreen = props => {
    const {
        historyScreenState,
        previousScores,
        difficulty,
        party,
        goBack,
        goToSettings,
        completeInquiryTutorial,
        tutorialCompleted,
        submitOverallScores,
        continueTo,
        continueRoute,
        progressGame,
    } = props;

    const [activeTopic, setActiveTopic] = useState(null);

    const committeeData = Copy.committees[difficulty];

    const possibleCommittees = committeeData.filter(
        committee => committee.party !== party,
    );

    const activeCommittee =
        activeTopic !== null
            ? possibleCommittees.filter(
                  committee => committee.interest === activeTopic,
              )[0]
            : null;

    const committeeTargets = {
        evidence: activeCommittee ? activeCommittee.value_evidence : 0,
        views: activeCommittee ? activeCommittee.value_views : 0,
    };

    const [step, setStep] = useState(0);
    const [currentGameScores, setCurrentGameScores] = useState(null);
    const [responseFeedback, setResponseFeedback] = useState(null);
    const [progressUpdateVisible, setProgressUpdateVisible] = useState(false);

    const [committeeMembers, setCommitteeMembers] = useState([]);
    const [committeeSelected, setCommitteeSelected] = useState(false);
    const [committeeResults, setCommitteeResults] = useState({});

    const modalVisible = progressUpdateVisible || !tutorialCompleted;

    useLayoutEffect(() => {
        if (historyScreenState.screen === INQUIRY_GAME_SCREEN) {
            setActiveTopic(historyScreenState.state.activeTopic);
            setCommitteeMembers(historyScreenState.state.committeeMembers);
            setStep(historyScreenState.state.step);
        }
    }, [historyScreenState]);

    const completeGame = () => {
        if (continueRoute === MAIN_GAME_SCREEN) {
            submitOverallScores(previousScores, currentGameScores);
            progressGame();
        }
        continueTo(continueRoute);
    };

    const toggleCommitteeMember = memberId => {
        if (committeeMembers.includes(memberId)) {
            setCommitteeMembers(
                committeeMembers.filter(member => member !== memberId),
            );
        } else {
            setCommitteeMembers([...committeeMembers, memberId]);
        }
    };

    const gatherEvidence = () => {
        const committeeScores = getCommitteeScores(
            committeeMembers,
            activeCommittee,
        );
        const gameResults = getCommitteeGameScores(
            committeeMembers,
            activeCommittee,
        );

        setCommitteeResults(committeeScores);
        setCurrentGameScores(gameResults.scores);
        setResponseFeedback(gameResults.feedback);
        setCommitteeSelected(true);
    };

    const clickBack = () => {
        if (step === 0) {
            return goBack();
        }

        setCommitteeMembers([]);
        setCommitteeSelected(false);
        setActiveTopic(null);
        setStep(0);
    };

    const inquiryPageClasses = classNames({
        'inquiry-game': true,
        'inquiry-game--hidden': modalVisible,
    });

    return (
        <>
            <div className={inquiryPageClasses} aria-hidden={modalVisible}>
                <IconButton
                    iconName="back"
                    size="small"
                    type="essential"
                    withSpacing={true}
                    onClick={() => clickBack()}
                    isDisabled={modalVisible || committeeSelected === true}
                />
                <Panel
                    flexDirection="column"
                    withPadding={true}
                    color="purple"
                    currentPage={`inquiry game ${steps[step]}`}
                    key={step}
                >
                    <div>
                        <Heading
                            headingType="h3"
                            colorScheme="dark"
                            withSpacing={false}
                        >
                            {Copy.heading[steps[step]]}{' '}
                        </Heading>
                        {step === 1 ? (
                            <div>
                                <Heading
                                    headingType="h4"
                                    withSpacing={false}
                                    colorScheme="dark"
                                    textAlign="center"
                                    size="medium"
                                >
                                    {activeCommittee.title}
                                </Heading>
                            </div>
                        ) : null}
                    </div>
                    {step === 0 ? (
                        <TopicDecision
                            options={possibleCommittees}
                            selectTopic={topic => {
                                setActiveTopic(topic);
                                setStep(1);
                            }}
                            modalVisible={modalVisible}
                        />
                    ) : (
                        <CommitteeSelection
                            committeeMembers={committeeMembers}
                            possibleMembers={activeCommittee.members}
                            toggleCommitteeMember={toggleCommitteeMember}
                            committeeSelected={committeeSelected}
                            committeeResults={committeeResults}
                            committeeTargets={committeeTargets}
                            gatherEvidence={() => gatherEvidence()}
                            showProgressUpdateModal={() =>
                                setProgressUpdateVisible(true)
                            }
                        />
                    )}
                </Panel>
                <IconButton
                    iconName="settings"
                    size="small"
                    type="essential"
                    withSpacing={true}
                    onClick={() =>
                        goToSettings(activeTopic, committeeMembers, step)
                    }
                    isDisabled={modalVisible || committeeSelected === true}
                />
            </div>
            {!tutorialCompleted && (
                <TutorialModalParent
                    screen="inquiry"
                    onClick={() => completeInquiryTutorial()}
                />
            )}
            {progressUpdateVisible === true && (
                <ProgressUpdateModalParent
                    previousScores={previousScores}
                    gameScores={currentGameScores}
                    feedback={responseFeedback}
                    onClick={() => completeGame()}
                />
            )}
        </>
    );
};

const mapStateToProps = state => {
    return {
        continueRoute: state.router.history[state.router.history.length - 2],
        historyScreenState: state.router.historyScreenState,
        tutorialCompleted:
            state.activeGame.tutorialsCompleted[INQUIRY_GAME_SCREEN],
        previousScores: state.activeGame.overallScores,
        difficulty: state.settings.gameSettings.difficulty,
        party: state.settings.gameSettings.party,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        goBack: () => {
            dispatch(goBack());
        },
        goToSettings: (activeTopic, committeeMembers, step) => {
            dispatch(
                setHistoryScreenState(INQUIRY_GAME_SCREEN, {
                    activeTopic,
                    committeeMembers,
                    step,
                }),
            );
            dispatch(goToScreen(SETTINGS_SCREEN));
        },
        continueTo: route => {
            dispatch(goToScreenAndReset(route));
        },
        completeInquiryTutorial: () => {
            dispatch(completeTutorial(INQUIRY_GAME_SCREEN));
        },
        submitOverallScores: (previousScores, gameScores) => {
            dispatch(updateOverallScores(previousScores, gameScores));
        },
        progressGame: () => {
            dispatch(progressTime('inquiry'));
        },
    };
};

const connected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(InquiryGameScreen);
export { connected as InquiryGameScreen };
